import type {
  ResponseAcademicRisk,
  StudentAcademicRisk,
  YearAcademicRisk,
} from '@/domains/academicRisk/academicRiskStudent.entity';

/**
 * Datos consolidados de todos los años y semestres
 */
interface ConsolidatedAcademicRiskData {
  students: StudentAcademicRisk[];
}

/**
 * Use Case para consolidar todos los datos de riesgo académico sin filtros temporales.
 *
 * Funcionalidad:
 * - Extrae todos los estudiantes de la estructura anidada año > semestre > estudiantes
 * - Consolida en un único array plano de estudiantes sin aplicar filtros
 * - Permite análisis global de todos los datos históricos disponibles
 *
 * Responsabilidades:
 * - Iterar todos los años disponibles en los datos de la API
 * - Iterar todos los semestres de cada año
 * - Aplanar la estructura anidada en un array único
 * - Mantener el orden cronológico de los datos
 * - Validar integridad de datos de entrada
 */
export class ConsolidateAllAcademicRiskDataUseCase {
  /**
   * Extrae todos los estudiantes de la estructura anidada año > semestre.
   *
   * @param yearData - Objeto completo con estructura {año: {semestre: [estudiantes]}}
   */
  private extractAllStudents(
    yearData: YearAcademicRisk,
  ): StudentAcademicRisk[] {
    const allStudents: StudentAcademicRisk[] = [];

    // Iterar por cada año
    for (const year in yearData) {
      const semesterData = yearData[year];

      // Iterar por cada semestre del año
      for (const semester in semesterData) {
        const students = semesterData[semester];

        if (Array.isArray(students)) {
          allStudents.push(...students);
        }
      }
    }

    return allStudents;
  }

  /**
   * Ejecuta la consolidación de todos los datos académicos sin filtros.
   *
   * @param apiData - Respuesta completa de la API con estructura año > semestre > estudiantes
   */
  execute(apiData: ResponseAcademicRisk | null): ConsolidatedAcademicRiskData {
    if (!apiData?.data) {
      return { students: [] };
    }

    const students = this.extractAllStudents(apiData.data);

    return { students };
  }
}
